import * as WebBrowser from "expo-web-browser";
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Image,
  Pressable,
  Linking,
  View,
  Platform,
  NativeModules,
  NativeEventEmitter,
} from "react-native";
import Container from "@/components/shared/AppContainer";
import Text from "@/components/shared/AppText";

import QRCode from "react-qr-code";
import AsyncStorage from "@react-native-async-storage/async-storage";

import { useAuth } from "@/components/AuthContext";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";

import { TextLink } from "@/components/shared/TextLink";
import OnboardingButton from "@/components/shared/OnboardingButton";
import { GetAppStore, GetPlayStore } from "@/components/shared/Buttons";
import ShareEventButton from "@/components/shared/ShareEventButton";
import Link from "@/components/shared/Link";
import RtmpForm from "@/components/shared/RtmpForm";
import useSubscribeToEventUpdates from "@/components/hooks/useSubscribeToEventUpdates";
import AppContent from "@/components/shared/AppContent";
import AppHeader from "@/components/shared/AppHeader";
import { Ionicons } from "@expo/vector-icons";
import { createTest } from "@/components/http/events";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import { openStreamWindow } from "@/components/Utils";
import {
  desktopStudioPlatform,
  hasDesktopStudio,
  useDesktopStudioClosed,
} from "@/components/desktop/desktopApp";
import { eventLivePageBase, eventTitle } from "@/components/globalUtils";
import tw from "twrnc";
import { t } from "@/localization";
import { useSafeAreaFrame } from "react-native-safe-area-context";

const openLink = async (url) => {
  await WebBrowser.openBrowserAsync(url);
};

const TestScreen = ({ route, navigation }) => {
  const {
    slug,
    event: initialEvent,
    relatedEvent,
    rtmpActive: rtmpActiveInitial,
  } = route.params || {};

  const {
    broadcaster,
    profileLoaded,
    prefer_multiple_tests,
    getOpenAppFromWebUrl,
    custom_domain,
  } = useAuth();

  const isMobileUX = useIsMobileUX();

  const [testEvent, setTestEvent] = useState(
    typeof initialEvent === "object" ? initialEvent : { broadcaster, slug }
  );
  const listenerRef = useRef(null);

  const [rtmpActive, setRtmpActive] = useState(rtmpActiveInitial);
  const [magicUrl, setmagicUrl] = useState("");
  const [streamWindowOpened, setStreamWindowOpened] = useState(false);

  useEffect(() => {
    (async () => {
      if (!slug && !initialEvent && profileLoaded) {
        const e = await createTest(broadcaster, !!prefer_multiple_tests);
        setTestEvent(e);
      }
    })();
  }, [slug, initialEvent, broadcaster, prefer_multiple_tests, profileLoaded]);

  // Cold loads of getapp/:slug render before the account is known; fill in
  // the broadcaster once it is so the test event actually loads.
  useEffect(() => {
    if (slug && broadcaster && !testEvent?.broadcaster) {
      setTestEvent((current) => ({ ...current, broadcaster, slug }));
    }
  }, [slug, broadcaster, testEvent?.broadcaster]);

  const { event, loading } = useSubscribeToEventUpdates(testEvent, true);

  useEffect(() => {
    const openStream = async () => {
      if (event && event.stream_key && !streamWindowOpened) {
        if (Platform.OS !== "web" || hasDesktopStudio()) {
          setStreamWindowOpened(true);
          await openStreamWindow(event);
        }
      }
    };

    openStream();
  }, [event, streamWindowOpened]);

  useEffect(() => {
    if (Platform.OS === "web" && event && event.slug) {
      const fetchMagicUrl = async () => {
        const redir = await getOpenAppFromWebUrl(`/getapp/${event.slug}`);
        setmagicUrl(redir);
      };

      fetchMagicUrl();
    }
  }, [event]);

  // Effect for adding the event listener
  useEffect(() => {
    const addEventListener = async () => {
      if (
        event &&
        event.stream_key &&
        Platform.OS !== "web" &&
        !listenerRef.current
      ) {
        const emitter =
          Platform.OS === "ios"
            ? new NativeEventEmitter(NativeModules.StartStream)
            : new NativeEventEmitter(NativeModules.StreamModule);

        console.log("adding listener");
        if (listenerRef.current) {
          console.log("Remove current listener");
          listenerRef.current.remove();
        }
        listenerRef.current = emitter.addListener(
          "StreamDone",
          async (data) => {
            console.log("STREAM DONE TestScreen", data);

            const reviewAsked = await AsyncStorage.getItem("reviewAsked");
            if (!reviewAsked) {
              navigation.navigate("AskReview", { relatedEvent });
              await AsyncStorage.setItem("reviewAsked", "true");
            } else {
              if (relatedEvent && !relatedEvent.is_test) {
                navigation.replace("PayWall", {
                  event: relatedEvent,
                  slug: relatedEvent.slug,
                });
              } else {
                navigation.popToTop();
              }
            }
          }
        );
      }
    };

    addEventListener();

    return () => {
      if (listenerRef.current) {
        console.log("removing listener");
        listenerRef.current.remove();
        listenerRef.current = null;
      }
    };
  }, [event?.stream_key]);

  useDesktopStudioClosed(event?.stream_key, ({ wentLive }) => {
    if (!wentLive) return;
    if (relatedEvent && !relatedEvent.is_test) {
      navigation.replace("PayWall", {
        event: relatedEvent,
        slug: relatedEvent.slug,
      });
    } else {
      navigation.popToTop();
    }
  });

  // Tests are always in your own account (also while you work in someone
  // else's): your custom domain, and the test's own broadcaster.
  const domain = custom_domain || eventLivePageBase;
  const url = event?.alias
    ? `${domain}${event.alias}`
    : `${domain}${event?.broadcaster || broadcaster}/${event?.slug}`;

  const linkPress = useCallback(() => {
    if (Platform.OS === "web") {
      window.open(url, "_blank");
    } else Linking.openURL(url);
  }, [url]);

  const openAppStore = async () => {
    await openLink(
      magicUrl ||
        "https://apps.apple.com/us/app/eventlive-event-live/id1457137042#?platform=iphone"
    );
  };

  const openPlayStore = async () => {
    await openLink(
      "https://play.google.com/store/apps/details?id=pro.eventlive&hl=en&gl=US"
    );
  };

  const isLive = event?.event_status === "live";

  // EventLive for Mac or Windows ("mac" / "windows", else null): the test
  // runs in the app's own Studio window instead of on a phone.
  const desktopStudio = desktopStudioPlatform();

  const { width: responsiveWidth } = useSafeAreaFrame();

  return (
    <>
      <Container>
        <AppHeader
          // title={title}
          leftButtonComponent={
            <Pressable
              onPress={() => {
                navigation.popToTop();
              }}
            >
              <Ionicons name="close-outline" size={30} style={tw`opacity-30`} />
            </Pressable>
          }
          hasLeftButton={true}
          hasRightButton={true}
          renderRightButton={() => {
            return loading && Platform.OS === "web" && <AppActivityIndicator />;
          }}
        />

        <AppContent>
          <View style={tw`flex-1 flex-grow mx-0 flex-row justify-center`}>
            {Platform.OS !== "web" && loading && (
              <AppActivityIndicator size="large" color="#F84123" />
            )}
            {Platform.OS === "web" && (
              <View style={tw`flex-1`}>
                {isLive && (
                  <View style={tw`p-5 flex-1`}>
                    <Text
                      style={[
                        tw`text-black text-[42px] font-extrabold text-left leading-[48px] mb-3`,
                        Platform.OS !== "web" && tw`text-2xl leading-[30px]`,
                      ]}
                    >
                      {event?.is_test
                        ? t("Your test is streaming")
                        : t("You are Live!")}
                    </Text>

                    <View
                      style={tw`flex-1 justify-center items-center self-center max-w-[800px]`}
                    >
                      <OnboardingButton
                        onPress={linkPress}
                        text={t("Watch Live")}
                        style={tw`w-full`}
                      />

                      <ShareEventButton
                        url={url}
                        title={eventTitle(event)}
                        text={t("Share")}
                        dark
                        bordered
                        style={tw`w-full self-center`}
                      />

                      <TextLink
                        text={url}
                        onPress={linkPress}
                        textStyle={tw`text-sm py-1.5`}
                      />
                    </View>
                  </View>
                )}

                {!isLive && (
                  <>
                    <View style={tw`px-5`}>
                      <Text
                        style={[
                          tw`text-black text-[42px] font-extrabold text-left leading-[48px] mb-3`,
                          Platform.OS !== "web" && tw`text-2xl leading-[30px]`,
                        ]}
                      >
                        {rtmpActive
                          ? t("RTMP Settings for {title}", {
                              title: eventTitle(event),
                            })
                          : desktopStudio === "windows"
                          ? t("Start a free test from this PC")
                          : desktopStudio === "mac"
                          ? t("Start a free test from this Mac")
                          : t("Let's set up your mobile app")}
                      </Text>

                      <Text
                        style={tw`opacity-50 text-black text-base font-medium text-left`}
                      >
                        {desktopStudio
                          ? t(
                              "Your test opens in the Studio window. Pick your camera and microphone there, then press Start Test."
                            )
                          : t(
                              "Our native iOS and Android apps are the easiest way to go live."
                            )}
                      </Text>
                      {Platform.OS === "web" && (
                        <Link onPress={() => setRtmpActive(!rtmpActive)}>
                          {rtmpActive
                            ? t("Get EventLive app to go Live")
                            : t("Alternative ways to stream")}
                        </Link>
                      )}
                    </View>

                    {rtmpActive && (
                      <View style={tw`px-5`}>
                        <RtmpForm event={event} />
                      </View>
                    )}

                    {!rtmpActive && desktopStudio && (
                      <View style={tw`px-5 mt-6 max-w-[420px]`}>
                        <OnboardingButton
                          text={t("Open Studio")}
                          icon="videocam-outline"
                          onPress={() => openStreamWindow(event)}
                        />
                      </View>
                    )}

                    {!rtmpActive && Platform.OS === "web" && !desktopStudio && (
                      <View style={tw`flex-1 flex-col justify-center`}>
                        <View
                          style={tw`w-[376px] self-start text-center justify-center flex-col pl-5`}
                        >
                          {isMobileUX && magicUrl && (
                            <View>
                              <OnboardingButton
                                text={t("Open our App to Go Live")}
                                onPress={() => {
                                  Linking.openURL(
                                    magicUrl ||
                                      `https://eventlive.app.link/streamFor?streamKey=${event?.stream_key}&$ios_nativelink=true`
                                  );
                                }}
                              />
                            </View>
                          )}

                          {!isMobileUX && magicUrl && (
                            <View>
                              <Pressable onPress={openAppStore}>
                                <QRCode
                                  value={magicUrl}
                                  style={tw`max-w-[200px]`}
                                />
                              </Pressable>
                              <View style={tw`flex-row flex-0 mt-10 mb-10`}>
                                <GetAppStore
                                  onPress={openAppStore}
                                  style={tw`mr-2.5`}
                                />

                                <GetPlayStore onPress={openPlayStore} />
                              </View>
                            </View>
                          )}
                        </View>
                        <Image
                          source={require("@/assets/getapp.png")}
                          resizeMode="contain"
                          style={[
                            tw`w-full self-center`,
                            {
                              height:
                                Math.min(responsiveWidth, 720) * (463 / 637),
                            },
                          ]}
                        />
                      </View>
                    )}
                  </>
                )}
              </View>
            )}
          </View>
        </AppContent>
      </Container>
    </>
  );
};

export default TestScreen;
